import { AxiosError } from 'axios'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { Button, Form, TextInput } from '@/components/core'
import { useAuth } from '@/contexts/AuthContext.tsx'
import { useLanguage } from '@/contexts/LanguageContext.tsx'
import validateTestCode from '@/features/Login/services/validateTestCode.ts'
import { classNameBuilder } from '@/helpers/classNameBuilder'
import translations from '@/translations'
import { ROUTES } from '@/types/routes.ts'

type LoginFormData = {
  code: string
}

interface LoginFormProps {
  className?: string
}

export function LoginForm({ className }: LoginFormProps) {
  const navigate = useNavigate()

  const { language } = useLanguage()
  const { handleTestCodeValidated } = useAuth()

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<LoginFormData>()

  const onSubmit = async (data: LoginFormData) => {
    setIsSubmitting(true)
    setError(null)

    try {
      const response = await validateTestCode({
        code: data.code,
        locale: language
      })

      handleTestCodeValidated(data.code, response)

      setTimeout(() => {
        navigate(
          response.showOnboardingQuestions
            ? ROUTES.ONBOARDING.ONBOARDING_SURVEY
            : ROUTES.ONBOARDING.WELCOME
        )
      }, 0)
    } catch (err) {
      const axiosError = err as AxiosError<{ error: string; message: string }>
      const errorCode = axiosError.response?.data?.error ?? 'generic_error'

      if (errorCode === 'unauthorized') {
        setError(translations.unauthorized_error_login_form)
      } else if (errorCode === 'test_application_invalid') {
        setError(translations.test_in_progress_error_login_form)
      } else {
        setError(errorCode)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Form
      className={classNameBuilder('space-y-8', className)}
      onSubmit={handleSubmit(onSubmit)}
    >
      <TextInput
        {...register('code', { required: true })}
        label={translations.access_code_label_login_form}
        error={errors.code ? 'Required' : error || undefined}
        onChange={(e) => {
          e.target.value = e.target.value.toUpperCase()
          register('code').onChange(e)
        }}
      />
      <Button.Translated
        translationKey="access_button_login_form"
        type="submit"
        color="flat-dark"
        size="md"
        pending={isSubmitting}
      />
    </Form>
  )
}

export default LoginForm
